/**
 * @summary - Component that Provides Stock Register Listing Page
 * @author Yuvasri S
 * @since 2024-07-25
 */
import React, { useState, useEffect } from "react";
import { Box, Stack, Typography } from "@mui/material";
import CustomAgGrid from "../commoncomponents/CustomAgGrid";
import { useNavigate } from "react-router-dom";
import { DefaultCard } from "../../assets/styled-components/card";
import {
  getAuthToken,
  getFacilityId,
  getcompanyId,
} from "../../utility/tokenUtility";
import axios from "axios";
import CreateNewButton from "../commoncomponents/CreateNewButton";
import RefreshExportButtons from "../commoncomponents/RefreshExportButtons";
import { FemsGraph, MasterGraph } from "../../utility/apiURLS";
import CloseButton from "../commoncomponents/CloseButton";
import PageTitle from "../commoncomponents/PageTitle";
import SectionTitle from "../commoncomponents/SectionTitle";
import { ErrorChips, SuccessChips } from "../../assets/styled-components/chips";
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';

const columns = [
  { field: "itemcode", headerName: "Item Code", width: 150 },
  { field: "itemDescription", headerName: "Item Description", width: 180 },
  { field: "itemType", headerName: "Item Type", width: 150 },
  { field: "workgroup", headerName: "Work Group", width: 180 },
  { field: "compitembrand", headerName: "Brand/Make", width: 150 },
  { field: "manufacturer", headerName: "Manufacturer", width: 180 },
  {
    field: "isactive", headerName: "Status", width: 150,
    cellRenderer: (params) => {
      if (params.value === 'Active') {
        return <SuccessChips label="Active" />;
      } else {
        return <ErrorChips label="Inactive" />;
      }
    }
  },
  { field: "currentstocklevelformate", headerName: "Current Stock Level", width: 180 },

];

const StockRegisterList = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "STRG001");

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const facilityId = getFacilityId("facilityId");

  const fetchStockRegisters = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${MasterGraph}/api/GraphStockRegister/GetStockRegisterByFacilityid/${facilityId}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`,
        config
      );
      console.log('response.data', response.data.items)
      setRows(response.data.items);
      setFilteredRows(response.data.items);
      setRowCount(response.data.totalCount);
      setExportCount(response.data.totalCount)

    } catch (error) {
      console.error("Error fetching stock register:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchStockRegisters();
  },  [filterModel, sortModel, pageIndex, pageSize]);


  const fetchExportData = async () => {
   
    try {
      const response = await axios.get(
        `${MasterGraph}/api/GraphStockRegister/GetExportData/${facilityId}?filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`,
        config
      );
      setExportData(response.data)


    } catch (error) {
      console.error("Error fetching stock register:", error);
    } 
  };

  const handleCreateStockRegister = () => {
    navigate("register");
  };

  const handleEdit = (rowData) => {
    navigate("edit", {
      state: { ...rowData, isEditMode: "edit" },
    });
  };

  const handleView = (rowData) => {
    navigate("edit", {
      state: { ...rowData, isViewMode: "view" },
    });
  };

  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };

  return (
    <>
      <PageTitle title="Stock Register" />
      <DefaultCard>
        <Box
          mb={1}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
          }}
        >
          <SectionTitle title="Stock Register List" />
          <Stack direction={"row"} spacing={1.5}>
            {access?.canCreate && <CreateNewButton onClick={handleCreateStockRegister} />}
            <RefreshExportButtons
               exportFunction={fetchExportData}
              exportData={exportData}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title={"Stock Register"}
            />
          </Stack>
        </Box>
        <CustomAgGrid
          rows={filteredRows}
          columns={columns}
          handleEdit={access?.canEdit && handleEdit}
          handleView={access?.canView && handleView}
          setGridApi={setGridApi}
          isLoading={isLoading}
          pagination={false}
          setFilterModel={setFilterModel}
          setSortModel={setSortModel}
          totalCount={rowCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
        />

      </DefaultCard>
      <CloseButton />
    </>
  );
};

export default StockRegisterList;
